import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { AnalyticsService } from '../../../../common/services/analytics-wrapper/analytics.service';
import { BreakpointState } from '@angular/cdk/layout';
import { ButtonComponent } from '../../atoms/button/button.component';
import { CommonUtility } from '../../../../common/utils/commonUtils';
import { INavMenuItem } from '../../../../common/models/interfaces/IInclusion';
import { MatExpansionModule } from '@angular/material/expansion';
import { MenuitemsService } from '../../../../common/services/menu-items/menu-items.service';
import { NgClass } from '@angular/common';
import { ROUTER_CONSTANTS } from '../../../../common/constants/router.constants';
import { RenderLabelPipe } from '../../../../common/pipes/render-label/render-label.pipe';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { SymphonyDrilldownFacade } from '../../../../common/facades/symphony-drilldown/symphony-drilldown.facade';
import { ViewportService } from '../../../../common/services/viewport/viewport.service';

@Component({
  selector: 'app-hamburger-menu',
  templateUrl: './hamburger-menu.component.html',
  styleUrls: ['./hamburger-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, RenderLabelPipe, ButtonComponent, MatExpansionModule],
})
export class HamburgerMenuComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() menuItems: INavMenuItem[];
  @Output() hamburgerClosed: EventEmitter<boolean> = new EventEmitter();
  @Output() unitAPIError: EventEmitter<any> = new EventEmitter();
  @ViewChild('closeBtn', { static: false, read: ElementRef })
  closeBtn: ElementRef;
  handler: () => void;
  escHandler: () => void;
  isOpened: boolean = false;
  ngUnsubscribe: Subject<void> = new Subject<void>();
  isMobile: boolean = true;
  symphonyDrilldownFacade: SymphonyDrilldownFacade;

  constructor(
    private readonly _renderer: Renderer2,
    private readonly analyticsService: AnalyticsService,
    private readonly menuItemService: MenuitemsService,
    private readonly viewportService: ViewportService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly injector: Injector,
  ) {
    this.symphonyDrilldownFacade = new SymphonyDrilldownFacade(this.injector);
    this.symphonyDrilldownFacade.ngUnsubscribe$ = this.ngUnsubscribe;
    this.subscribeToUnitApiErrorState();
    this.open();
  }

  /**
   * Angular lifcycle hook
   */
  ngOnInit(): void {
    this.viewportService.mobileObserver
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(200),
        distinctUntilChanged(),
      )
      .subscribe((result: BreakpointState) => {
        this.isMobile = result.matches;
        this.cdr.detectChanges();
      });
  }

  /**
   * Subscribe to Unit API error for Symphony/Symphony Plus
   */
  subscribeToUnitApiErrorState(): void {
    this.symphonyDrilldownFacade.unitApiErrorSubject$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isErrorState: boolean) => {
        if (isErrorState) {
          this.unitAPIError.emit();
          this.close();
        }
      });
  }

  /**
   * Lifecycle method
   */
  ngAfterViewInit(): void {
    this.closeBtn?.nativeElement
      ?.querySelector('#hamburgerMenuCloseBtn')
      ?.focus();
    setTimeout(() => this.documentClick());
  }

  /**
   * Closes Hamburger menu when user clicks outside of the sidenav
   */
  documentClick(): void {
    this.handler = this._renderer.listen(
      document,
      'click',
      this._handleClick.bind(this),
    );
    // close hamburger if it's open and esc is clicked
    this.escHandler = this._renderer.listen(document, 'keydown.esc', () => {
      if (this.isOpened) {
        this.close();
      }
    });
  }

  /**
   * On Header Nav menu link clicked
   * @param menuItem menu Item object
   */
  onMenuItemClick(menuItem: INavMenuItem): void {
    switch (menuItem?.id) {
      case 'dcCreateCase':
        this.router.navigate([`${ROUTER_CONSTANTS.createCase}`]);
        this.close();
        break;
      default:
        this.menuItemService
          .onMenuItemClick(menuItem, this.symphonyDrilldownFacade)
          .pipe(takeUntil(this.ngUnsubscribe))

          .subscribe();
    }
  }

  /**
   * Closes Hamburger menu when user clicks outside of the sidenav
   * @param event : Browser event object
   * @returns boolean
   */
  _handleClick(event: any): boolean {
    if (
      event?.target?.closest('#overlay') ||
      event?.target?.getAttribute('id') === 'hamburgerControl' ||
      event?.target?.className?.includes('cdk-overlay-backdrop')
    ) {
      event.preventDefault();
      return false;
    }

    if (this.isOpened) {
      this.close();
    }

    return true;
  }

  /**
   * Opens the hamburger menu
   */
  open(): void {
    CommonUtility.addRemoveScrollClassFromPanel(
      this._renderer,
      false,
      'remove-scroll',
    );
    this.isOpened = true;
  }

  /**
   * Closes Hamburger menu
   */
  _close(): void {
    CommonUtility.addRemoveScrollClassFromPanel(
      this._renderer,
      true,
      'remove-scroll',
    );
    this.isOpened = false;
  }

  /**
   * Closes the hamburger menu and notifies parent
   */
  close(): void {
    this._close();
    this.hamburgerClosed.emit(true);
  }

  /**
   * Angular ngOnDestroy lifecycle hook
   */
  ngOnDestroy(): void {
    if (this.handler) {
      this.handler();
    }
    if (this.escHandler) {
      this.escHandler();
    }
    if (this.isOpened) {
      // If parent destroys the component by clicking on cross in header
      this.close();
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Analytics call
   * @param url URL
   * @param parentServiceName parentServiceName
   * @param childServiceName childServiceName
   */
  tagOnNavClick(
    url: string = '#',
    parentServiceName: string = '',
    childServiceName: string = '',
  ): void {
    if (url) {
      const linkName: string = `${parentServiceName}${
        childServiceName ? ` - ${childServiceName}` : ''
      }`;
      this.analyticsService.createLinkClickTagObject(
        url,
        linkName,
        'Left Navigation Menu Clicked',
        'Internal',
        { link_section: 'Left Navigation Menu' },
      );
    }
  }
}
